import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import type { Location } from 'react-router';

import type { TilePermissionResolvers } from './availability/types';

export const URL_TILE_SEARCH_PARAM = 'tile';

export type UrlTilePayload = {
    readonly [key: string]: unknown;
    readonly type: string;
};

export type UrlTileMutationOptions = {
    readonly replace?: boolean;
};

export type UrlTileRenderContext = {
    readonly closeTile: (slotKey: string, options?: UrlTileMutationOptions) => void;
    readonly location: Location;
    readonly openTile: (payload: UrlTilePayload, options?: UrlTileMutationOptions) => void;
    readonly updateTile: (payload: UrlTilePayload, options?: UrlTileMutationOptions) => void;
};

export type UrlTileDefinition = {
    readonly maxWidth?: string;
    readonly minWidth?: string;
    readonly parseValue: (value: string) => UrlTilePayload | undefined;
    readonly renderPayload: (payload: UrlTilePayload, context: UrlTileRenderContext) => ReactNode | undefined;
    readonly serializePayload: (payload: UrlTilePayload) => string | undefined;
    readonly slotKeyForPayload: (payload: UrlTilePayload) => string | undefined;
    readonly tileKeyForPayload: (payload: UrlTilePayload) => string | undefined;
    readonly type: string;
    readonly width?: string;
};

export type UrlTileDefinitionInput<TPayload extends UrlTilePayload, TProps> = {
    readonly maxWidth?: string;
    readonly minWidth?: string;
    readonly parseTokens: (tokens: readonly string[]) => unknown;
    readonly render: (props: TProps) => ReactNode;
    readonly schema: {
        readonly safeParse: (value: unknown) =>
            | {
                  readonly data: TPayload;
                  readonly success: true;
              }
            | {
                  readonly success: false;
              };
    };
    readonly serialize: (payload: TPayload) => string;
    readonly slotKey: (payload: TPayload) => string;
    readonly tileKey: (payload: TPayload) => string;
    readonly toProps: (payload: TPayload, context: UrlTileRenderContext) => TProps;
    readonly type: TPayload['type'];
    readonly width?: string;
};

export type ParsedUrlTile = {
    readonly definition: UrlTileDefinition;
    readonly payload: UrlTilePayload;
    readonly slotKey: string;
    readonly tileKey: string;
    readonly value: string;
};

export type UrlTileLegacyRewriteParams = {
    readonly definitions: readonly UrlTileDefinition[];
    readonly hadCanonicalTileParams: boolean;
    readonly location: Location;
    readonly searchParams: URLSearchParams;
};

export type UrlTileChromeRenderer = ({
    content,
    context,
    tile,
}: {
    readonly content: ReactNode;
    readonly context: UrlTileRenderContext;
    readonly tile: ParsedUrlTile;
}) => ReactNode;

export type UrlTileLegacyRewriter = {
    readonly rewrite: (params: UrlTileLegacyRewriteParams) => URLSearchParams | undefined;
};

export type UrlTileModule = {
    readonly definitions: readonly UrlTileDefinition[];
    readonly legacyRewriters?: readonly UrlTileLegacyRewriter[];
    readonly permissionResolvers?: TilePermissionResolvers;
    readonly Provider?: FunctionComponent<PropsWithChildren>;
    readonly renderTileChrome?: UrlTileChromeRenderer;
};
